import { useCallback, useEffect, useMemo, useState } from 'react';

import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { useAddDiagnosisCodeMutation } from '@/store/slices/api/billingApiSlice';
import { useUpdatePatientMutation } from '@/store/slices/api/patientsApiSlice';
import {
  useAddCountersMutation,
  usePatchReportInClinicEpisodeMutation,
  usePatchTransmissionReportMutation,
  useUpdateCountersMutation,
  useUpdateMultipleNotesMutation,
  useUpdateMultipleOverridesMutation,
  useUpdateProgrammedParametersMutation
} from '@/store/slices/api/reportsApiSlice';
import {
  addToReportUpdateFields,
  clearReportUpdateFieldCategory,
  resetReportUpdateFields,
  updateReportUpdateFieldsSaveStatus
} from '@/store/slices/transmissionReports/transmissionReportsSlice';

import { ReportLocationEnum, UpdateNoteParams } from '@/types/alert';
import { Counters } from '@/types/counters';
import { ReportUpdateFieldKey, ReportUpdateFields } from '@/types/transmissionReport';
import {
  MultipleVendorDataOverrides,
  UpdateDeviceLeadDto,
  VendorDataOverrides
} from '@/types/vendorTransmissionReport';

import { useGetTransmissionReportDetails } from './useGetTransmissionReportDetails';

const useSaveUpdatedReportFields = () => {
  const [pendingSaveFields, setPendingSaveFields] = useState<{
    id: number;
    fields: ReportUpdateFields;
  } | null>(null);

  const [
    updateTransmissionReport,
    {
      isSuccess: isUpdateTransmissionReportSuccess,
      isError: isUpdateTransmissionReportError,
      isUninitialized: isUpdateTransmissionReportUninitialized
    }
  ] = usePatchTransmissionReportMutation();
  const [
    addCounters,
    {
      isSuccess: isAddCounterSuccess,
      isError: isAddCounterError,
      isUninitialized: isAddCounterUninitialized
    }
  ] = useAddCountersMutation();
  const [
    updateCounters,
    {
      isSuccess: isUpdateCounterSuccess,
      isError: isUpdateCounterError,
      isUninitialized: isUpdateCounterUninitialized
    }
  ] = useUpdateCountersMutation();
  const [
    updateMultipleNotes,
    {
      isSuccess: isUpdateMultipleNotesSuccess,
      isError: isUpdateMultipleNotesError,
      isUninitialized: isUpdateMultipleNotesUninitialized
    }
  ] = useUpdateMultipleNotesMutation();
  const [
    updateInClinicEpisode,
    {
      isSuccess: isUpdateInClinicEpisodeSuccess,
      isError: isUpdateInClinicEpisodeError,
      isUninitialized: isUpdateInClinicEpisodeUninitialized
    }
  ] = usePatchReportInClinicEpisodeMutation();
  const [
    updateProgrammedParameters,
    {
      isSuccess: isUpdateProgrammedParametersSuccess,
      isError: isUpdateProgrammedParametersError,
      isUninitialized: isUpdateProgrammedParametersUninitialized
    }
  ] = useUpdateProgrammedParametersMutation();
  const [
    updateMultipleOverrides,
    {
      isSuccess: isUpdateOverridesSuccess,
      isError: isUpdateOverridesError,
      isUninitialized: isUpdateOverridesUninitialized
    }
  ] = useUpdateMultipleOverridesMutation();
  const [
    updatePatientDetails,
    {
      isSuccess: isUpdatePatientDetailsSuccess,
      isError: isUpdatePatientDetailsError,
      isUninitialized: isUpdatePatientDetailsUninitialized
    }
  ] = useUpdatePatientMutation();
  const [
    addDiagnosisCodeToBilling,
    {
      isSuccess: isAddDiagnosisCodeSuccess,
      isError: isAddDiagnosisCodeError,
      isUninitialized: isAddDiagnosisCodeUninitialized
    }
  ] = useAddDiagnosisCodeMutation();

  const isCountersSuccess = useMemo(
    () => isAddCounterSuccess || isUpdateCounterSuccess,
    [isAddCounterSuccess, isUpdateCounterSuccess]
  );
  const isCountersError = useMemo(
    () => isAddCounterError || isUpdateCounterError,
    [isAddCounterError, isUpdateCounterError]
  );
  const isCountersUninitialized = useMemo(
    () => isAddCounterUninitialized && isUpdateCounterUninitialized,
    [isAddCounterUninitialized, isUpdateCounterUninitialized]
  );
  const { reportUpdateFields } = useAppSelector((state) => state.transmissionReports);
  const dispatch = useAppDispatch();
  const { data } = useGetTransmissionReportDetails();

  // if the fields were updated successfully, clear the fields from the store
  // else try that again during next autosave
  useEffect(() => {
    handleFieldUpdate(
      {
        key: 'counters',
        isSuccess: isCountersSuccess,
        isError: isCountersError,
        fieldValue: pendingSaveFields?.fields.counters
      },
      dispatch,
      setPendingSaveFields,
      data?.transmissionReport.id === pendingSaveFields?.id
    );
  }, [
    dispatch,
    isCountersSuccess,
    isCountersError,
    pendingSaveFields?.fields.counters,
    pendingSaveFields?.id,
    data?.transmissionReport.id
  ]);

  useEffect(() => {
    handleFieldUpdate(
      {
        key: 'programmedParameters',
        isSuccess: isUpdateProgrammedParametersSuccess,
        isError: isUpdateProgrammedParametersError,
        fieldValue: pendingSaveFields?.fields.programmedParameters
      },
      dispatch,
      setPendingSaveFields,
      data?.transmissionReport.id === pendingSaveFields?.id
    );
  }, [
    dispatch,
    isUpdateProgrammedParametersSuccess,
    isUpdateProgrammedParametersError,
    pendingSaveFields?.fields.programmedParameters,
    pendingSaveFields?.id,
    data?.transmissionReport.id
  ]);

  useEffect(() => {
    handleFieldUpdate(
      {
        key: 'inClinicEpisode',
        isSuccess: isUpdateInClinicEpisodeSuccess,
        isError: isUpdateInClinicEpisodeError,
        fieldValue: pendingSaveFields?.fields.inClinicEpisode
      },
      dispatch,
      setPendingSaveFields,
      data?.transmissionReport.id === pendingSaveFields?.id
    );
  }, [
    dispatch,
    isUpdateInClinicEpisodeSuccess,
    isUpdateInClinicEpisodeError,
    pendingSaveFields?.fields.inClinicEpisode,
    pendingSaveFields?.id,
    data?.transmissionReport.id
  ]);

  useEffect(() => {
    handleFieldUpdate(
      {
        key: 'report',
        isSuccess: isUpdateTransmissionReportSuccess,
        isError: isUpdateTransmissionReportError,
        fieldValue: pendingSaveFields?.fields.report
      },
      dispatch,
      setPendingSaveFields,
      data?.transmissionReport.id === pendingSaveFields?.id
    );
  }, [
    data?.transmissionReport.id,
    dispatch,
    isUpdateTransmissionReportError,
    isUpdateTransmissionReportSuccess,
    pendingSaveFields?.fields.report,
    pendingSaveFields?.id
  ]);

  useEffect(() => {
    handleFieldUpdate(
      {
        key: 'notes',
        isSuccess: isUpdateMultipleNotesSuccess,
        isError: isUpdateMultipleNotesError,
        fieldValue: pendingSaveFields?.fields.notes
      },
      dispatch,
      setPendingSaveFields,
      data?.transmissionReport.id === pendingSaveFields?.id
    );
  }, [
    dispatch,
    isUpdateMultipleNotesSuccess,
    isUpdateMultipleNotesError,
    pendingSaveFields?.fields.notes,
    pendingSaveFields?.id,
    data?.transmissionReport.id
  ]);

  useEffect(() => {
    handleFieldUpdate(
      {
        key: 'overrides',
        isSuccess: isUpdateOverridesSuccess,
        isError: isUpdateOverridesError,
        fieldValue: pendingSaveFields?.fields.overrides
      },
      dispatch,
      setPendingSaveFields,
      data?.transmissionReport.id === pendingSaveFields?.id
    );
  }, [
    dispatch,
    isUpdateOverridesSuccess,
    isUpdateOverridesError,
    pendingSaveFields?.fields.overrides,
    pendingSaveFields?.id,
    data?.transmissionReport.id
  ]);

  useEffect(() => {
    if (
      isUpdatePatientDetailsSuccess &&
      (!reportUpdateFields.patient?.diagnosisOption || isAddDiagnosisCodeSuccess)
    ) {
      dispatch(clearReportUpdateFieldCategory({ key: 'patient' }));
    } else if (isUpdatePatientDetailsError || isAddDiagnosisCodeError) {
      console.log('Error updating patient details');
      dispatch(updateReportUpdateFieldsSaveStatus('failed'));
      dispatch(
        addToReportUpdateFields({
          key: 'patient',
          value: pendingSaveFields?.fields.patient
        })
      );
      setPendingSaveFields(null);
    }
  }, [
    dispatch,
    isUpdatePatientDetailsSuccess,
    isUpdatePatientDetailsError,
    isAddDiagnosisCodeSuccess,
    isAddDiagnosisCodeError,
    reportUpdateFields.patient?.diagnosisOption,
    pendingSaveFields?.fields.patient
  ]);

  // check if all updates were successful.
  // an api call is either successful or uninitialized
  const isAllUpdatesSuccess = useMemo(() => {
    return (
      (isCountersSuccess || isCountersUninitialized) &&
      (isUpdateMultipleNotesSuccess || isUpdateMultipleNotesUninitialized) &&
      (isUpdateProgrammedParametersSuccess || isUpdateProgrammedParametersUninitialized) &&
      (isUpdateInClinicEpisodeSuccess || isUpdateInClinicEpisodeUninitialized) &&
      (isUpdateTransmissionReportSuccess || isUpdateTransmissionReportUninitialized) &&
      (isUpdateOverridesSuccess || isUpdateOverridesUninitialized) &&
      (isUpdatePatientDetailsSuccess || isUpdatePatientDetailsUninitialized) &&
      (!reportUpdateFields.patient?.diagnosisOption ||
        isAddDiagnosisCodeSuccess ||
        isAddDiagnosisCodeUninitialized)
    );
  }, [
    isCountersSuccess,
    isCountersUninitialized,
    isUpdateMultipleNotesSuccess,
    isUpdateMultipleNotesUninitialized,
    isUpdateProgrammedParametersSuccess,
    isUpdateProgrammedParametersUninitialized,
    isUpdateInClinicEpisodeSuccess,
    isUpdateInClinicEpisodeUninitialized,
    isUpdateTransmissionReportSuccess,
    isUpdateTransmissionReportUninitialized,
    isUpdateOverridesSuccess,
    isUpdateOverridesUninitialized,
    isUpdatePatientDetailsSuccess,
    isUpdatePatientDetailsUninitialized,
    reportUpdateFields.patient?.diagnosisOption,
    isAddDiagnosisCodeSuccess,
    isAddDiagnosisCodeUninitialized
  ]);

  useEffect(() => {
    if (!data || !pendingSaveFields || Object.keys(pendingSaveFields).length < 1) return;

    // check if counters were updated
    if (
      pendingSaveFields.fields.counters &&
      Object.keys(pendingSaveFields.fields.counters).length > 0 &&
      data.transmissionReport.id === pendingSaveFields.id
    ) {
      if (data.transmissionReport.counters?.Id) {
        updateCounters({
          transmissionReportId: data.transmissionReport.id,
          transmissionReportCountersUpdateParams: {
            countersobject: pendingSaveFields.fields.counters
          }
        });
      } else {
        addCounters({
          transmissionReportId: data.transmissionReport.id,
          transmissionReportCountersCreateParams: {
            countersobject: pendingSaveFields.fields.counters
          }
        });
      }
      // add the counter changes to overrides
      const counterOverridesObject = Object.keys(pendingSaveFields.fields.counters).reduce(
        (acc, key) => {
          acc[`deviceCounters.${key}`] = pendingSaveFields.fields.counters
            ? pendingSaveFields.fields.counters[key as keyof Counters]
            : '';
          return acc;
        },
        {} as Record<string, string | null | undefined>
      );
      updateMultipleOverrides({
        transmissionReportId: data.transmissionReport.id,
        overrides: counterOverridesObject as MultipleVendorDataOverrides
      });
    }

    // check if report params were updated
    if (
      pendingSaveFields.fields.report &&
      Object.keys(pendingSaveFields.fields.report).length > 0 &&
      data.transmissionReport.id === pendingSaveFields.id
    ) {
      updateTransmissionReport({
        id: data.transmissionReport.id,
        ...pendingSaveFields.fields.report
      });
    }

    // check if episode params were updated
    if (
      pendingSaveFields.fields.inClinicEpisode &&
      Object.keys(pendingSaveFields.fields.inClinicEpisode).length > 0 &&
      data.transmissionReport.id === pendingSaveFields.id
    ) {
      updateInClinicEpisode({
        reportId: data.transmissionReport.id,
        ...pendingSaveFields.fields.inClinicEpisode
      });
    }

    // check if programmed parameters were updated
    if (
      pendingSaveFields.fields.programmedParameters &&
      Object.keys(pendingSaveFields.fields.programmedParameters).length > 0 &&
      data.transmissionReport.id === pendingSaveFields.id
    ) {
      updateProgrammedParameters({
        transmissionReportId: data.transmissionReport.id,
        programmedParametersUpdateParams: pendingSaveFields.fields.programmedParameters
      });
    }

    // check if notes were updated
    if (
      pendingSaveFields.fields.notes &&
      Object.keys(pendingSaveFields.fields.notes).length > 0 &&
      data.transmissionReport.id === pendingSaveFields.id
    ) {
      const notesUpdateParams = buildNotesUpdateParams(pendingSaveFields.fields.notes);
      updateMultipleNotes({
        transmissionReportId: data.transmissionReport.id,
        notes: notesUpdateParams
      });
    }

    // check if overrides was updated
    if (
      pendingSaveFields.fields.overrides &&
      Object.keys(pendingSaveFields.fields.overrides).length > 0 &&
      data.transmissionReport.id === pendingSaveFields.id
    ) {
      const newOverrides: MultipleVendorDataOverrides = {};
      const deviceLeads = groupDeviceLeads(pendingSaveFields.fields.overrides);
      newOverrides.deviceLeads = deviceLeads;
      // create a new object with all the params except the ones that are not device leads

      Object.entries(pendingSaveFields.fields.overrides).forEach(([key, val]) => {
        if (!key.startsWith('deviceLeads') && pendingSaveFields.fields.overrides) {
          (newOverrides as any)[key] = val; // Filter out the 'deviceLeads' prefix
        }
      });
      console.log('newOverrides', newOverrides);
      updateMultipleOverrides({
        transmissionReportId: data.transmissionReport.id,
        overrides: newOverrides
      });
    }

    // check if patient details were updated
    if (
      pendingSaveFields.fields.patient &&
      Object.keys(pendingSaveFields.fields.patient).length > 0 &&
      data.transmissionReport.id === pendingSaveFields.id
    ) {
      if (pendingSaveFields.fields.patient.diagnosisOption) {
        addDiagnosisCodeToBilling({
          diagnosisCode: {
            value: pendingSaveFields.fields.patient.diagnosisOption,
            label: pendingSaveFields.fields.patient.diagnosisOption
          },
          reportId: data.transmissionReport.id,
          billingId: data.transmissionReport.billing?.id ?? ''
        });
      }
      const { practitionerId, ...patientFields } = pendingSaveFields.fields.patient as any;

      updatePatientDetails({
        patient: {
          id: data.transmissionReport.patient.id,
          ...(practitionerId && { practitioner: { id: practitionerId } }),
          ...patientFields
        },
        reportId: data.transmissionReport.id
      });
    }
  }, [
    addCounters,
    addDiagnosisCodeToBilling,
    data,
    pendingSaveFields,
    updateCounters,
    updateInClinicEpisode,
    updateMultipleNotes,
    updateMultipleOverrides,
    updatePatientDetails,
    updateProgrammedParameters,
    updateTransmissionReport
  ]);

  const callback = useCallback(() => {
    if (!data?.transmissionReport.id) return;
    // if there are no fields to update or no data, skip autosave
    if (Object.keys(reportUpdateFields).length < 1) {
      console.log('autosave skip');
      return;
    }

    if (pendingSaveFields && Object.keys(pendingSaveFields).length > 0) {
      console.log('pending save fields found. skipping autosave');
      return;
    }

    console.log('fields updated. autosaving..');
    dispatch(updateReportUpdateFieldsSaveStatus('saving'));
    setPendingSaveFields({ id: data.transmissionReport.id, fields: reportUpdateFields });
    dispatch(resetReportUpdateFields());
  }, [data?.transmissionReport.id, dispatch, pendingSaveFields, reportUpdateFields]);

  useEffect(() => {
    if (isAllUpdatesSuccess) {
      dispatch(updateReportUpdateFieldsSaveStatus('saved'));
      setPendingSaveFields(null);
    }
  }, [dispatch, isAllUpdatesSuccess]);

  return callback;
};

const buildNotesUpdateParams = (notes: Record<ReportLocationEnum, string>) => {
  const res: UpdateNoteParams[] = Object.entries(notes).map(([key, val]) => {
    if (key.includes(ReportLocationEnum.Alert)) {
      return {
        note: val,
        location: ReportLocationEnum.Alert,
        alertId: key.split('.')[1]
      };
    }
    return {
      note: val,
      location: key as ReportLocationEnum
    };
  });
  return res;
};

function groupDeviceLeads(data: Partial<VendorDataOverrides>): Partial<UpdateDeviceLeadDto>[] {
  const result: Partial<UpdateDeviceLeadDto>[] = [];

  Object.keys(data).forEach((key) => {
    const [prefix, id, field] = key.split('.');
    if (prefix !== 'deviceLeads') return;
    const deviceLeadId = parseInt(id);
    const index = result.findIndex((lead) => lead.deviceLeadId === deviceLeadId);
    if (index === -1) {
      result.push({
        deviceLeadId,
        [field as keyof UpdateDeviceLeadDto]: data[key as keyof VendorDataOverrides]
      });
    } else {
      const existingLead: any = result[index];
      existingLead[field as keyof UpdateDeviceLeadDto] = data[key as keyof VendorDataOverrides];
    }
  });

  return Object.values(result);
}

type UpdateFieldParams = {
  key: ReportUpdateFieldKey;
  isSuccess: boolean;
  isError: boolean;
  fieldValue?: any;
};

const handleFieldUpdate = (
  { key, isSuccess, isError, fieldValue }: UpdateFieldParams,
  dispatch: any,
  setPendingSaveFields: React.Dispatch<
    React.SetStateAction<{ id: number; fields: ReportUpdateFields } | null>
  >,
  isSameReport: boolean
) => {
  if (isSuccess) {
    setPendingSaveFields((prev) => {
      if (prev && prev.fields) {
        const { [key]: _, ...rest } = prev.fields;
        return { id: prev.id, fields: rest };
      }
      return prev;
    });
  } else if (isError) {
    console.log(`Error updating ${key}`);
    dispatch(updateReportUpdateFieldsSaveStatus('failed'));
    if (isSameReport) {
      dispatch(
        addToReportUpdateFields({
          key,
          value: fieldValue
        })
      );
    }
    setPendingSaveFields(null);
  }
};

export default useSaveUpdatedReportFields;
